/**
 * Redeem Codes API Client — authenticated account-scoped list.
 *
 * Talks to `GET /api/redeem-codes/mine`, which is `authRequired` on
 * the backend and scopes every DB read by `user_id = req.user.id`.
 * The client sends nothing but pagination/context — never a userId
 * — because identity is JWT-derived.
 *
 * Model A (see backend audit): codes are pre-owned at admin
 * issuance. There is no "claim" / "add" endpoint on the backend
 * for good reason — the schema doesn't support unowned codes.
 * Do NOT add one here.
 */

import { authClient } from '../auth/authClient.js';

const REDEEM_CODES_MINE = '/redeem-codes/mine';

function correlationId() {
  return `ev-user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * List the signed-in user's own reimbursement codes.
 *
 * @returns {Promise<
 *   | { ok: true, codes: object[], correlationId: string }
 *   | { ok: false, status?: number, error: string, message: string, correlationId: string }
 * >}
 */
export async function listMyRedeemCodes() {
  const cid = correlationId();
  try {
    const res = await authClient.get(REDEEM_CODES_MINE, {
      headers: { 'X-Correlation-Id': cid },
    });
    const body = res.data || {};
    return {
      ok: true,
      codes: Array.isArray(body.codes) ? body.codes : [],
      correlationId: cid,
    };
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    return {
      ok: false,
      status,
      error: data.error || 'LIST_FAILED',
      message: data.message || err?.message || 'Could not load your redeem codes.',
      correlationId: cid,
    };
  }
}
